//#region IMPORTS
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { InstoreTemplateRow } from "../../models/instoreTemplate";
import { useMemo, useState } from "react";
import WeekOrderThemeItems from "./WeekOrderThemeItems";
import classNames from "../../helpers/classNames";
//#endregion

//#region INTERFACE
interface ThemeGroup {
  theme_id: number;
  theme: string;
  items: InstoreTemplateRow[];
}

interface Props {
  groups: ThemeGroup[];
  quantityByRowId: Map<number, number>;
  onRowAdd: (row: InstoreTemplateRow, quantity: number) => void;
  loading: boolean;
  disabled?: boolean;
  showStoreFormat?: boolean;
}
//#endregion

export default function WeekOrderItemsList({
  groups,
  quantityByRowId,
  onRowAdd,
  loading,
  disabled = false,
  showStoreFormat = false,
}: Props) {
  //#region SETUP
  const [openThemeId, setOpenThemeId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const isSearching = searchTerm.trim().length > 0;

  // Token-based fuzzy match across theme, article name, and custom fields.
  // Each whitespace-separated token must occur in the row haystack — order
  // independent, case insensitive.
  const filteredGroups = useMemo(() => {
    if (!isSearching) return groups;
    const tokens = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter((row) => {
          const haystack = `${row.theme ?? ""} ${row.article_name ?? ""} ${(
            row.custom_fields ?? []
          ).join(" ")}`.toLowerCase();
          return tokens.every((tok) => haystack.includes(tok));
        }),
      }))
      .filter((g) => g.items.length > 0);
  }, [groups, searchTerm, isSearching]);
  //#endregion

  //#region LOGIC
  const toggleTheme = (themeId: number) => {
    setOpenThemeId((current) => (current === themeId ? null : themeId));
  };
  //#endregion

  //#region RENDER
  if (loading && groups.length === 0)
    return <p className="mt-6 p-6 text-gray-500">Loading…</p>;

  if (groups.length === 0)
    return (
      <p className="mt-6 p-6 text-gray-500">No items in this template.</p>
    );

  return (
    <div className="mt-6 space-y-3">
      {/* Search */}
      <div className="relative w-1/3">
        <MagnifyingGlassIcon
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"
          aria-hidden="true"
        />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by theme, article…"
          className="block w-full rounded-md border-0 py-2 pl-10 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm"
        />
        {isSearching && (
          <button
            type="button"
            onClick={() => setSearchTerm("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100"
            aria-label="Clear search"
          >
            <XMarkIcon className="h-5 w-5 text-gray-400" />
          </button>
        )}
      </div>

      {isSearching && filteredGroups.length === 0 && (
        <p className="p-6 text-gray-500">No matches.</p>
      )}

      {filteredGroups.map((group) => {
        const isOpen = openThemeId === group.theme_id;
        const itemsInBasketCount = group.items.filter((item) =>
          quantityByRowId.has(item.id)
        ).length;

        return (
          <div
            key={group.theme_id ?? group.theme}
            className="rounded-lg border border-gray-200 bg-white overflow-hidden"
          >
            {/* Theme header */}
            <div className="flex items-center justify-between gap-4 px-6 py-4">
              <button
                type="button"
                onClick={() => toggleTheme(group.theme_id)}
                className="flex items-center gap-3 min-w-0 flex-1 hover:opacity-70 text-left"
              >
                <ChevronDownIcon
                  className={classNames(
                    "h-5 w-5 text-gray-500 transition-transform",
                    isOpen ? "rotate-180" : "rotate-0"
                  )}
                  aria-hidden="true"
                />
                <span className="truncate text-lg font-semibold text-gray-900">
                  {group.theme || "—"}
                </span>
                <span className="text-sm text-gray-500">
                  ({group.items.length})
                </span>
                {itemsInBasketCount > 0 && (
                  <span className="inline-flex items-center justify-center rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold h-6 min-w-6 px-2">
                    {itemsInBasketCount}
                  </span>
                )}
              </button>
            </div>

            {/* Items (virtualized) */}
            {isOpen && (
              <WeekOrderThemeItems
                items={group.items}
                quantityByRowId={quantityByRowId}
                onRowAdd={onRowAdd}
                disabled={disabled}
                showStoreFormat={showStoreFormat}
              />
            )}
          </div>
        );
      })}
    </div>
  );
  //#endregion
}
